import React, { useState } from 'react';
import { Button, Collapse, IconButton } from '@mui/material';
import {
  ExpandMore, ExpandLess, Description, Send, Save, Add, MoreVert,
  PictureAsPdf, Search
} from '@mui/icons-material';
import {
  Container,
  DocumentsSection,
  DocumentList,
  DocumentItem,
  DocumentTitle,
  EditorContainer,
  DropdownButton,
  ResearchPanelWrapper
} from './EditDocumentsContainer.styles';
import { ResearchContainer } from '@containers/ResearchContainer';
import EditorTable from '@components/Editor/EditorTable';

// Mock document list
export const mockDocuments = [
  { id: 1, title: 'Non-Disclosure Agreement', type: 'docx' },
  { id: 2, title: 'Employment Contract - Draft', type: 'docx' },
  { id: 3, title: 'Service Agreement with ABC Corp', type: 'pdf' },
  { id: 4, title: 'License Agreement', type: 'docx' },
  { id: 5, title: 'Property Lease - 123 Main St', type: 'pdf' },
];

// Mock content for each document
const mockDocumentContents = {
  1: `NON-DISCLOSURE AGREEMENT (NDA)

This Non-Disclosure Agreement ("Agreement") is entered into on [Date] by and between [Company Name], with a principal office at [Address] ("Disclosing Party"), and [Recipient Name], with a principal office at [Address] ("Receiving Party").

1. DEFINITIONS
"Confidential Information" refers to any and all proprietary information, data, trade secrets, technical information, and business plans disclosed by the Disclosing Party.

2. PURPOSE
The Receiving Party agrees to use the Confidential Information only for evaluating a potential business relationship with the Disclosing Party.

3. OBLIGATIONS OF RECEIVING PARTY
The Receiving Party agrees to keep all disclosed information confidential and take all reasonable steps to protect the information from unauthorized access.

4. EXCLUSIONS
Confidential Information does not include information that is already in the public domain, known to the Receiving Party before disclosure, or disclosed by a third party with the right to do so.

5. USE OF CONFIDENTIAL INFORMATION
The Receiving Party shall not use the Confidential Information for any purpose other than evaluating the business opportunity or fulfilling the terms of this Agreement.

6. TERM
This Agreement shall remain in effect for a period of five (5) years from the date of signing, unless terminated earlier by mutual agreement.

7. RETURN OF MATERIALS
Upon termination of the Agreement, the Receiving Party agrees to return all materials containing Confidential Information to the Disclosing Party.

8. NO LICENSE
Nothing in this Agreement grants the Receiving Party any rights or licenses to the Confidential Information.

9. REMEDIES
In the event of a breach of this Agreement, the Disclosing Party shall be entitled to seek injunctive relief, damages, and any other legal remedy.

10. GOVERNING LAW
This Agreement shall be governed by and construed in accordance with the laws of [Jurisdiction].

IN WITNESS WHEREOF, the parties have executed this Agreement as of the date first written above.

___________________________
Disclosing Party

___________________________
Receiving Party`,

  2: `EMPLOYMENT AGREEMENT

This Employment Agreement ("Agreement") is made and entered into on [Date] by and between [Company Name] ("Employer") and [Employee Name] ("Employee").

1. POSITION AND DUTIES
The Employee shall serve as [Job Title] and shall perform all duties as reasonably assigned by the Employer. The Employee agrees to work diligently and in accordance with Employer's policies.

2. COMMENCEMENT
This Agreement will commence on [Start Date] and will continue until terminated by either party in accordance with the terms herein.

3. COMPENSATION
The Employee shall receive a salary of $[Amount] per year, payable biweekly, and may be eligible for additional bonuses or incentives based on performance.

4. WORK HOURS
The standard workweek shall consist of [X] hours, from [Time] to [Time], Monday through Friday. Overtime may be required based on business needs.

5. BENEFITS
The Employee shall be entitled to participate in the Employer's benefits programs, including health insurance, retirement plans, and paid vacation, in accordance with company policies.

6. VACATION AND LEAVE
The Employee is entitled to [X] days of paid vacation per year, and may request leave for personal or medical reasons in accordance with company policy.

7. CONFIDENTIALITY
The Employee agrees not to disclose any confidential or proprietary information belonging to the Employer during or after employment.

8. TERMINATION
Either party may terminate this Agreement with [X] days' written notice. The Employer may terminate the Employee's employment immediately for cause, including but not limited to misconduct or breach of duties.

9. NON-COMPETE CLAUSE
For a period of [X] months following termination, the Employee agrees not to engage in any business that directly competes with the Employer within [Geographic Area].

10. GOVERNING LAW
This Agreement shall be governed by the laws of [Jurisdiction], and any disputes shall be resolved through arbitration.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the date first written above.

___________________________
Employer

___________________________
Employee`,

  3: `SERVICE AGREEMENT WITH ABC CORP

This Service Agreement ("Agreement") is made effective as of [Date] between ABC Corp, with its principal place of business at [Address] ("Client"), and [Service Provider], located at [Address] ("Provider").

1. SERVICES
Provider agrees to perform the services described in Exhibit A, including but not limited to [List specific services].

2. PAYMENT
Client shall pay Provider $[Amount] per hour for services rendered. Payment will be made monthly upon receipt of invoice, due within 15 days.

3. TERM
This Agreement shall commence on [Start Date] and shall remain in effect for a period of [X] months unless terminated earlier by either party.

4. CONFIDENTIALITY
Both parties agree to keep all confidential information shared during the term of this Agreement confidential and use it only for the purposes outlined herein.

5. INDEPENDENT CONTRACTOR STATUS
The Provider is an independent contractor and not an employee of the Client. As such, the Provider is responsible for their own taxes, benefits, and insurance.

6. MATERIALS AND EQUIPMENT
Unless otherwise specified, the Provider will be responsible for providing any materials or equipment required to perform the services.

7. WARRANTIES AND REPRESENTATIONS
The Provider warrants that all services will be performed in a professional manner in accordance with industry standards.

8. TERMINATION
Either party may terminate this Agreement upon thirty (30) days' written notice, with or without cause.

9. LIABILITY
The Client shall not be liable for any damages arising from the Provider's performance of services except where such damages result from gross negligence or willful misconduct.

10. GOVERNING LAW
This Agreement shall be governed by and construed in accordance with the laws of [Jurisdiction].

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.

___________________________
Client

___________________________
Provider`,

  4: `LICENSE AGREEMENT

This License Agreement ("Agreement") is made as of [Date] between [Licensor Name], with offices at [Address], and [Licensee Name], with offices at [Address].

1. GRANT OF LICENSE
Licensor hereby grants Licensee a non-exclusive, non-transferable license to use the licensed materials described in Exhibit A, subject to the terms and conditions of this Agreement.

2. FEES
Licensee agrees to pay a license fee of $[Amount] per year, payable in advance on the Effective Date and thereafter on each anniversary of the Effective Date.

3. TERM
The term of this license shall begin on [Start Date] and shall remain in effect for [X] years, unless terminated earlier pursuant to the provisions of this Agreement.

4. RENEWAL
This Agreement may be renewed for additional terms of [X] years upon mutual written consent of the parties.

5. INTELLECTUAL PROPERTY RIGHTS
Licensor retains all intellectual property rights in the licensed materials. Licensee shall not claim ownership or modify the materials in any way without prior written consent.

6. LIMITATIONS ON USE
Licensee shall not sublicense, distribute, or sell the licensed materials to any third party without the prior written consent of Licensor.

7. WARRANTIES
Licensor warrants that it has the legal right to grant the license and that the licensed materials do not infringe the intellectual property rights of any third party.

8. TERMINATION
Either party may terminate this Agreement for cause, including a material breach by the other party, with thirty (30) days' notice.

9. CONFIDENTIALITY
Both parties agree to keep all confidential information disclosed during the term of this Agreement confidential.

10. GOVERNING LAW
This Agreement shall be governed by and construed in accordance with the laws of [Jurisdiction].

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.

___________________________
Licensor

___________________________
Licensee`,

  5: `PROPERTY LEASE AGREEMENT

This Lease Agreement ("Agreement") is entered into on [Date] between [Landlord Name] ("Landlord") and [Tenant Name] ("Tenant").

1. PREMISES
Landlord agrees to lease to Tenant the property located at [Address], consisting of [X] square feet of commercial space.

2. TERM
The term of this lease shall begin on [Start Date] and continue for [X] months, with an option to renew for an additional [X] years.

3. RENT
Tenant agrees to pay monthly rent of $[Amount], due on the 1st of each month. Late payments will incur a penalty of $[Amount] per day.

4. SECURITY DEPOSIT
Tenant shall pay a security deposit of $[Amount], to be refunded at the end of the lease term, subject to the condition of the property.

5. USE OF PREMISES
Tenant shall use the premises only for [Specific Purpose] and shall not use the premises for any illegal or unauthorized activities.

6. MAINTENANCE
Tenant shall be responsible for maintaining the interior of the leased premises, including repairs and cleaning. The Landlord shall maintain the exterior and structural integrity of the property.

7. INSURANCE
Tenant is required to carry property insurance for their business operations. Landlord shall maintain insurance on the building.

8. TERMINATION
This Agreement may be terminated by either party with thirty (30) days' written notice, or immediately upon a material breach of the Agreement by either party.

9. RIGHT OF ENTRY
Landlord reserves the right to enter the premises during normal business hours for inspection or repairs with reasonable prior notice to Tenant.

10. GOVERNING LAW
This Agreement shall be governed by and construed in accordance with the laws of [Jurisdiction].

IN WITNESS WHEREOF, the parties have executed this Lease Agreement as of the Effective Date.

___________________________
Landlord

___________________________
Tenant`,
};



export default function EditDocumentsContainer() {
  const [selectedDocument, setSelectedDocument] = useState(1);
  const [documentContent, setDocumentContent] = useState(mockDocumentContents[1]);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isResearchCollapsed, setIsResearchCollapsed] = useState(true);

  const handleDocumentSelect = (id) => {
    setSelectedDocument(id);
    setDocumentContent(mockDocumentContents[id] || '');
  };

  const handleAddMockDocument = () => {
    const newId = mockDocuments.length + 1;
    const newDoc = {
      id: newId,
      title: `Mock Document ${newId}`,
      type: 'docx',
    };
    mockDocuments.push(newDoc);
    mockDocumentContents[newId] = `This is the content of Mock Document ${newId}.`;

    setSelectedDocument(newId);
    setDocumentContent(mockDocumentContents[newId]);
  };

  return (
    <Container>
      <DocumentsSection>

        {/* Sidebar Toggle Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px', padding: '5px' }}>
          <div style={{ padding: '5px' }}>
            <DropdownButton onClick={() => setIsCollapsed(!isCollapsed)}>
              <IconButton size="small">
                <Description />
              </IconButton>
            </DropdownButton>
          </div>
          <div style={{ padding: '5px' }}>
            <DropdownButton onClick={() => setIsResearchCollapsed(!isResearchCollapsed)}>
              <IconButton size="small">
                <Search />
              </IconButton>
            </DropdownButton>
          </div>
        </div>

        {/* Document List */}
        <Collapse in={!isCollapsed} orientation="horizontal">
          <DocumentList>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>My Documents</h3>
            <Button
              size="small"
              onClick={handleAddMockDocument}
              sx={{ backgroundColor: 'var(--secondary-main)', '&:hover': { backgroundColor: 'var(--secondary-light)' } }}
              startIcon={<Add />}
            >
              Document
            </Button>
            {mockDocuments.map((doc) => (
              <DocumentItem
                key={doc.id}
                isActive={doc.id === selectedDocument}
                onClick={() => handleDocumentSelect(doc.id)}
              >
                <span className="material-icons" style={{ color: 'var(--secondary-main)', fontSize: '20px' }}>
                  {doc.type === 'pdf' ? <PictureAsPdf /> : <Description />}
                </span>
                <DocumentTitle>{doc.title}</DocumentTitle>
                <Button size="small" sx={{ minWidth: 'unset', padding: '4px' }}>
                  <MoreVert style={{ fontSize: '18px' }} />
                </Button>
              </DocumentItem>
            ))}
          </DocumentList>
        </Collapse>

        {/* Editor */}
        <EditorContainer>
          <EditorTable content={documentContent} />
        </EditorContainer>

        {/* Research Sidebar */}
        <ResearchPanelWrapper isCollapsed={isResearchCollapsed}>
          <Collapse in={!isResearchCollapsed} orientation="horizontal">
              <ResearchContainer />
          </Collapse>
        </ResearchPanelWrapper>

      </DocumentsSection>
    </Container>
  );
}
